import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import EditorService, { Editor } from 'cio/services/editor';
import BeeService, { Template } from 'cio/services/bee';
import LayoutModel from 'cio/models/layout';
import TemplateModel from 'cio/models/template';
import { BeeTemplate } from 'cio/data/bee-templates';
import { isEmpty, isNone, isPresent } from '@ember/utils';
import { ComposerNewsletterTemplate } from 'cio/utils/composer/composer-editable-entity';
import { ComposerSection } from 'cio/components/action-composer';
import { tracked } from '@glimmer/tracking';
import { InAppStaticTemplate } from 'cio/services/in-app-static-templates';
import { DEFAULT_IN_APP_TEMPLATE_BODY } from 'cio/utils/in-app/preview';

export default class TemplateController extends Controller {
  queryParams = [
    {
      currentTab: { as: 'tab' },
    },
  ];

  @service editor!: EditorService;
  @service bee!: BeeService;

  editableEntity!: ComposerNewsletterTemplate;

  composerType = null;
  selectedEditor = null;

  @tracked isComposerDirty = false;
  @tracked currentTab = ComposerSection.Editor;

  get isDirty() {
    return this.editableEntity.template.hasDirtyAttributes || this.isComposerDirty;
  }

  @computed('editableEntity.newsletter.isSent')
  get _doneRoute() {
    let root = 'broadcasts.newsletter';
    return this.editableEntity.newsletter.isSent ? `${root}.overview.content` : `${root}.setup.content`;
  }

  @computed('editableEntity.{template.editor,defaultTemplate.editor}', 'selectedEditor')
  get editorName() {
    if (isPresent(this.editableEntity.template.editor)) {
      return this.editableEntity.template.editor;
    }

    let defaultTemplateEditor = this.editableEntity.defaultTemplate.editor;

    return isPresent(defaultTemplateEditor) ? defaultTemplateEditor : this.selectedEditor;
  }

  @computed('editorName')
  get isActionComposerV2() {
    return this.editorName === Editor.VISUAL && this.editableEntity.isEmail;
  }

  didSelectTemplateTask = task(async (template: TemplateModel | BeeTemplate | InAppStaticTemplate) => {
    if (template instanceof InAppStaticTemplate) {
      this.editableEntity.template.set(
        'bodyJson',
        JSON.stringify({
          carta_content: template.body,
        }),
      );
    } else {
      if (template instanceof BeeTemplate) {
        // Bee templates' JSON and HTML is fetched async, on demand.
        await template.fetchContent();
      } else {
        this.editableEntity.template.set('bodyAmp', template.bodyAmp);
      }

      let { body, bodyJson, layout, editor } = template;

      this.editableEntity.template.setProperties({
        body,
        bodyJson,
        layout: await layout, // "template.layout" is an async relationship, may need to be awaited
        editor,
      });
    }

    this.set('selectedEditor', template.editor);
    this.setEditor();
  });

  didSelectLayoutTask = task(async (layout: LayoutModel, editor: Editor) => {
    this.editableEntity.template.setProperties({ editor, layout });

    this.set('selectedEditor', editor);
    this.setEditor();
  });

  @action
  save() {
    let { editorName } = this;

    // moving the push editor from simple to rich progressively
    if (editorName === Editor.SIMPLE) {
      editorName = Editor.RICH;
    }

    this.editableEntity.template.set('editor', editorName);
    return this.editableEntity.template.save();
  }

  @action
  startFromScratch(editor: Editor) {
    this.set('selectedEditor', editor);
    this.setEmptyTemplateState(editor);
    this.setEditor();
  }

  @action
  onDirtyChange(isDirty: boolean) {
    this.isComposerDirty = isDirty;
  }

  setEmptyTemplateState(editor: Editor) {
    if (this.editableEntity.isInApp && editor === Editor.VISUAL) {
      this.editableEntity.template.setProperties({
        bodyJson: JSON.stringify({ carta_content: DEFAULT_IN_APP_TEMPLATE_BODY }),
      });
    }
  }

  setEditor() {
    let editor = this.editorName;

    if (this.editableEntity.isEmail) {
      if (isEmpty(editor)) {
        // If there is no editor selected, check whether there is a persisted
        // Bee state for this template. It's possible a customer encountered Bee
        // errors without having saved the editor selection, and then clicked
        // "Save and reload". In this case, we need to make sure to go straight
        // to the drag-and-drop composer.
        // The composer then will take care of restoring the template content
        // from localStorage.
        let persistedJson = this.bee.getPersistedStateFor(this.editableEntity.template as Template);
        if (!isNone(persistedJson)) {
          editor = Editor.DRAG_AND_DROP;
          this.editableEntity.template.set('editor', Editor.DRAG_AND_DROP);
        }
      }

      this.set('composerType', this.editor.getEmailComposerForEditor(editor as Editor));
    } else if (this.editableEntity.isInApp) {
      this.set('composerType', this.editor.getInAppComposerForEditor(editor as Editor));
    } else {
      // For all other editor types: push, slack, twilio, etc.
      this.set('composerType', this.editableEntity.newsletter.channel);
    }
  }
}
